import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Breadcrumb, BreadcrumbItem, Alert, Button, Badge } from 'reactstrap';
import { toast } from 'react-toastify';
import QuoteToolNavBar from "../molecules/NavBar.js";
import LoadingSpinner from "../atoms/loading/LoadingSpinner.js";
import UserInfoSection from "../molecules/UserInfoSection.js";
import UserRolesSection from "../molecules/UserRolesSection.js";
import UserApiKeysSection from "../molecules/UserApiKeysSection.js";
import '../../css/UserDetailPage.css';

const UserDetailPage = ({ quoteToolClient, currentUserId }) => {
    const { userId } = useParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);

    useEffect(() => {
        fetchUser();
    }, [userId]);

    const fetchUser = async () => {
        try {
            setLoading(true);
            setError(null);
            const userData = await quoteToolClient.getUserById(userId);
            setUser(userData);
        } catch (err) {
            console.error('Error fetching user:', err);
            setError('Failed to load user details');
            toast.error('Failed to load user details');
        } finally {
            setLoading(false);
        }
    };

    const handleUserUpdate = (updatedUser) => {
        setUser(updatedUser);
    };

    const handleToggleStatus = async () => {
        try {
            setUpdatingStatus(true);
            const updatedUser = await quoteToolClient.patchUser({
                userId: user.id,
                patch: { isDisabled: !user.isDisabled }
            });
            setUser(updatedUser);
            toast.success(`User ${updatedUser.isDisabled ? 'disabled' : 'enabled'} successfully`);
        } catch (error) {
            console.error('Error updating user status:', error);
            toast.error('Failed to update user status');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const isSelf = user && user.id === currentUserId;

    if (loading) {
        return (
            <>
                <QuoteToolNavBar />
                <Container className="mt-4">
                    <LoadingSpinner />
                </Container>
            </>
        );
    }

    if (error || !user) {
        return (
            <>
                <QuoteToolNavBar />
                <Container className="mt-4">
                    <Alert color="danger">{error || 'User not found'}</Alert>
                    <Link to="/admin/users">Back to Users</Link>
                </Container>
            </>
        );
    }

    return (
        <>
            <QuoteToolNavBar />
            <Container className="mt-4 user-detail-page">
                <Breadcrumb>
                    <BreadcrumbItem>
                        <Link to="/admin/users">Users</Link>
                    </BreadcrumbItem>
                    <BreadcrumbItem active>{user.email}</BreadcrumbItem>
                </Breadcrumb>

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-3">
                        <h2 className="mb-0">User Details</h2>
                        {user.isDisabled ? (
                            <Badge color="danger" className="fs-6">Disabled</Badge>
                        ) : (
                            <Badge color="success" className="fs-6">Active</Badge>
                        )}
                    </div>
                    <Button
                        color={user.isDisabled ? 'success' : 'danger'}
                        onClick={handleToggleStatus}
                        disabled={updatingStatus || isSelf}
                    >
                        {updatingStatus ? 'Updating...' : (user.isDisabled ? 'Enable User' : 'Disable User')}
                    </Button>
                </div>

                <Row>
                    <Col md={6} className="mb-3">
                        <UserInfoSection
                            user={user}
                            quoteToolClient={quoteToolClient}
                            onUpdate={handleUserUpdate}
                        />
                    </Col>
                    <Col md={6} className="mb-3">
                        <UserRolesSection
                            user={user}
                            quoteToolClient={quoteToolClient}
                            onUpdate={handleUserUpdate}
                            currentUserId={currentUserId}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col md={12} className="mb-3">
                        <UserApiKeysSection
                            user={user}
                            quoteToolClient={quoteToolClient}
                            onUpdate={handleUserUpdate}
                        />
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default UserDetailPage;
